import React, { createContext, useContext, useMemo } from "react";

/**
 * TileContext — Phase 3 Shell↔Tile decoupling (Zero Logic Change)
 *
 * Replaces prop-drilling of 24 props through V2_Main with a React Context.
 * During transition (PRs 3.1-3.3), context values coexist with existing props.
 * Tile reads: `ctx?.value ?? props.value` (context first, prop fallback).
 *
 * 5 SHARED props (userId, setActiveTabKey, setProfileLoading,
 * refreshHeaderTabCount, setRefreshHeaderTabCount) remain as V2_Main props
 * because V2_Main passes them to sibling components (Workspace, TicketDetailPage).
 */

// All values are optional during transition — Tile falls back to props if undefined
export interface TileContextValue {
  // ── Data props (flow down: Shell → Tile) ──
  userId?: any;
  isUserLoggedIn?: any;
  addServiceId?: any;
  userDataChanged?: any;
  currentUserData?: any;
  isCanceledPreset?: any;
  reccomendedData?: any;
  recommendationLoaded?: any;
  userWelcomeUpdated?: any;
  enableWwicCopilot?: boolean;
  refreshHeaderTabCount?: boolean;
  finalTileObject?: any;
  showErrorModal?: any;

  // ── Callback / setter props (flow up: Tile → Shell) ──
  setActiveTabKey?: any;
  setIrisUtterance?: any;
  setTileName?: any;
  setIrisAppName?: any;
  setSubSegment?: any;
  setSegment?: any;
  setAddServiceId?: any;
  setServiceDeleted?: any;
  setFormDataToIris?: any;
  setShowBot?: any;
  setChatBotClicked?: any;
  setProfileLoading?: any;
  setShowErrorModal?: any;
  setRecommendationLoaded?: any;
  setRefreshHeaderTabCount?: any;
  setIrisBotIconClicked?: any;
}

// Default = undefined (signals "context not yet populated" during transition)
const TileContext = createContext<TileContextValue | undefined>(undefined);
TileContext.displayName = "TileContext";

/**
 * Provider component — wraps the routes subtree in ShellRoutes.
 * Passes Shell's existing state/setters as context values.
 * Constructs a new object inside useMemo to ensure stable identity.
 */
export const TileContextProvider: React.FC<{
  value: TileContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => {
  const memoizedValue = useMemo(
    () => ({
      // Data props
      userId: value.userId,
      isUserLoggedIn: value.isUserLoggedIn,
      addServiceId: value.addServiceId,
      userDataChanged: value.userDataChanged,
      currentUserData: value.currentUserData,
      isCanceledPreset: value.isCanceledPreset,
      reccomendedData: value.reccomendedData,
      recommendationLoaded: value.recommendationLoaded,
      userWelcomeUpdated: value.userWelcomeUpdated,
      enableWwicCopilot: value.enableWwicCopilot,
      refreshHeaderTabCount: value.refreshHeaderTabCount,
      finalTileObject: value.finalTileObject,
      showErrorModal: value.showErrorModal,
      // Callback/setter props (stable refs from useState — rarely change)
      setActiveTabKey: value.setActiveTabKey,
      setIrisUtterance: value.setIrisUtterance,
      setTileName: value.setTileName,
      setIrisAppName: value.setIrisAppName,
      setSubSegment: value.setSubSegment,
      setSegment: value.setSegment,
      setAddServiceId: value.setAddServiceId,
      setServiceDeleted: value.setServiceDeleted,
      setFormDataToIris: value.setFormDataToIris,
      setShowBot: value.setShowBot,
      setChatBotClicked: value.setChatBotClicked,
      setProfileLoading: value.setProfileLoading,
      setShowErrorModal: value.setShowErrorModal,
      setRecommendationLoaded: value.setRecommendationLoaded,
      setRefreshHeaderTabCount: value.setRefreshHeaderTabCount,
      setIrisBotIconClicked: value.setIrisBotIconClicked,
    }),
    [
      // Data props
      value.userId,
      value.isUserLoggedIn,
      value.addServiceId,
      value.userDataChanged,
      value.currentUserData,
      value.isCanceledPreset,
      value.reccomendedData,
      value.recommendationLoaded,
      value.userWelcomeUpdated,
      value.enableWwicCopilot,
      value.refreshHeaderTabCount,
      value.finalTileObject,
      value.showErrorModal,
      // Callback/setter props (stable refs from useState — rarely change)
      value.setActiveTabKey,
      value.setIrisUtterance,
      value.setTileName,
      value.setIrisAppName,
      value.setSubSegment,
      value.setSegment,
      value.setAddServiceId,
      value.setServiceDeleted,
      value.setFormDataToIris,
      value.setShowBot,
      value.setChatBotClicked,
      value.setProfileLoading,
      value.setShowErrorModal,
      value.setRecommendationLoaded,
      value.setRefreshHeaderTabCount,
      value.setIrisBotIconClicked,
    ]
  );

  return (
    <TileContext.Provider value={memoizedValue}>
      {children}
    </TileContext.Provider>
  );
};

/**
 * Consumer hook — returns TileContext value or undefined if not inside Provider.
 * Tile uses: `const ctx = useTileContext(); const userId = ctx?.userId ?? props.userId;`
 */
export const useTileContext = (): TileContextValue | undefined => {
  return useContext(TileContext);
};
